import React, { useState, useEffect, useRef } from 'react';
import { 
  Clock, Check, AlertCircle, Loader2, Video, Image as ImageIcon, FileText,
  ExternalLink, Download, Filter, RefreshCw, Calendar, Trash2, Search, X, Copy, Play, Share2, Users,
  LayoutGrid, List as ListIcon, MoreHorizontal, Package, LogOut
} from 'lucide-react';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { HistoryItem, ImageHistoryItem, VideoHistoryItem, ListingHistoryItem } from '../../types';
import { API_BASE_URL, TOOL_COSTS } from '../../constants';
import { useToast } from '../ui/Toast';
import { ShareModal } from '../ui/ShareModal';
import { ConfirmModal } from '../ui/ConfirmModal';

interface HistoryProps {
  onCostIncurred?: (cost: number) => Promise<void>;
  checkCredits?: (cost: number) => boolean;
}

export const History: React.FC<HistoryProps> = ({ onCostIncurred, checkCredits }) => {
  const { addToast } = useToast();
  const { user, loading: isAuthLoading } = useAuth();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'video' | 'image' | 'listing'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<HistoryItem | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Share State
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [itemToShare, setItemToShare] = useState<{type: string, id: string, sharedWith?: string[]} | null>(null);
  
  // Confirm Modal State
  const [confirmConfig, setConfirmConfig] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => Promise<void>;
    isDestructive: boolean;
  }>({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: async () => {},
    isDestructive: false,
  });
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  // Download State to prevent spamming
  const [isDownloading, setIsDownloading] = useState(false);
  
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchHistory = async () => {
    try {
      const result = await apiService.getFullHistory();
      if (result.success) {
        setItems(result.history);
      } else {
        setError(result.error || "Failed to fetch history");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item: HistoryItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation(); 
    
    const isSharedWithMe = item.isSharedWithMe;
    
    setConfirmConfig({
        isOpen: true,
        title: isSharedWithMe ? "Remove from View" : "Delete Item",
        description: isSharedWithMe 
            ? "Are you sure you want to remove this shared item from your history? You can ask the owner to share it again if needed." 
            : "Are you sure you want to permanently delete this item? This action cannot be undone.",
        isDestructive: true,
        onConfirm: async () => {
            setIsConfirmLoading(true);
            try { 
                if (isSharedWithMe) {
                    const myEmail = user?.email;
                    if (myEmail) {
                        await apiService.revokeShareHistoryItem(item.type, item.id, myEmail);
                        addToast("Removed from shared items", 'info');
                    }
                } else {
                    await apiService.deleteHistoryItem(item.type, item.id); 
                    addToast("Item deleted", 'info'); 
                }
                
                // Update UI
                setItems(prev => prev.filter(i => i.id !== item.id));
                if (selectedItem?.id === item.id) setSelectedItem(null);
                setConfirmConfig(prev => ({ ...prev, isOpen: false }));
            } catch (err: any) { 
                addToast("Action failed: " + err.message, 'error'); 
                fetchHistory(); // Revert
            } finally {
                setIsConfirmLoading(false);
            }
        }
    });
  };

  const handleRetryVideo = async (item: VideoHistoryItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    // Check credits
    if (checkCredits && !checkCredits(TOOL_COSTS.video_generation)) return;
    
    if (!item.imageUrl) {
        addToast("Cannot retry: Source image URL missing.", 'error');
        return;
    }

    try {
        const result = await apiService.startVideoGeneration({
            referenceName: item.referenceName || item.storeName || "Retry Video",
            videoPrompt: item.videoPrompt,
            imageUrl: item.imageUrl,
            listingData: item.listingData
        });

        if (result.success) {
            if (onCostIncurred) await onCostIncurred(TOOL_COSTS.video_generation);
            addToast("Retry started successfully! Check history for the new job.", 'success');
            fetchHistory(); // Refresh to show new job
        } else {
             addToast("Retry failed: " + result.error, 'error');
        }
    } catch(err: any) {
        addToast("Retry error: " + err.message, 'error');
    }
  }

  const openShareModal = (item: HistoryItem, e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      if (item.isSharedWithMe) return; // Should not happen due to UI checks
      setItemToShare({ type: item.type, id: item.id, sharedWith: item.sharedWith });
      setShareModalOpen(true);
  };

  const submitShare = async (email: string) => {
      if (!itemToShare) return;
      try {
          const result = await apiService.toggleShareHistoryItem(itemToShare.type, itemToShare.id, email);
          if (result.success) {
              addToast(`Successfully shared with ${email}`, 'success');
              // Optimistically update list locally so modal updates if reopened
              setItems(prev => prev.map(i => {
                  if (i.id === itemToShare.id) {
                      const newShared = i.sharedWith ? [...i.sharedWith, email] : [email];
                      // Update modal state too
                      setItemToShare(curr => curr ? { ...curr, sharedWith: newShared } : null);
                      return { ...i, sharedWith: newShared };
                  }
                  return i;
              }));
          }
      } catch (err: any) {
          addToast("Failed to share: " + err.message, 'error');
          throw err;
      }
  };

  const revokeShare = async (email: string) => {
      if (!itemToShare) return;
      try {
          const result = await apiService.revokeShareHistoryItem(itemToShare.type, itemToShare.id, email);
          if (result.success) {
              addToast(`Removed access for ${email}`, 'success');
              // Update local state
              setItems(prev => prev.map(i => {
                  if (i.id === itemToShare.id) {
                      const newShared = i.sharedWith?.filter(e => e !== email) || [];
                      setItemToShare(curr => curr ? { ...curr, sharedWith: newShared } : null);
                      return { ...i, sharedWith: newShared };
                  }
                  return i;
              }));
          }
      } catch (err: any) {
          addToast("Failed to revoke: " + err.message, 'error');
          throw err;
      }
  }

  useEffect(() => {
    if (!isAuthLoading && user) {
        fetchHistory();
    } else if (!isAuthLoading && !user) {
        setLoading(false);
    }
  }, [user, isAuthLoading]);

  useEffect(() => {
    if (!user) return;
    
    pollingRef.current = setInterval(() => {
      setItems(current => {
        const hasActive = current.some(item => item.type === 'video' && ['pending', 'processing'].includes(item.status));
        if (hasActive) fetchHistory();
        return current;
      });
    }, 15000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [user]);

  // --- DOWNLOAD HELPER WITH PROXY ---
  const downloadFile = async (url: string, filename: string) => {
    const proxyUrl = `${API_BASE_URL}/api/proxy-download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`;
    const link = document.createElement('a');
    link.href = proxyUrl;
    link.download = filename; 
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadItem = async (item: HistoryItem, e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      if (isDownloading) return;
      setIsDownloading(true);

      try {
          if (item.type === 'video') {
              const vItem = item as VideoHistoryItem;
              if (vItem.url) await downloadFile(vItem.url, `video-${item.id}.mp4`);
          } else if (item.type === 'image') {
              const imgItem = item as ImageHistoryItem;
              if (Array.isArray(imgItem.output)) {
                  let count = 0;
                  for (const img of imgItem.output) {
                      await downloadFile(img.url, img.filename || `mockup-${count}.png`);
                      count++;
                      await new Promise(r => setTimeout(r, 1000));
                  }
              } else {
                  await downloadFile(imgItem.output as string, `image-${item.id}.png`);
              }
          }
      } catch (e) {
          console.error(e);
          addToast("Error downloading file(s).", 'error');
      } finally {
          setIsDownloading(false);
      }
  };

  const getJobName = (item: HistoryItem) => {
      if (item.type === 'video') return (item as VideoHistoryItem).referenceName || (item as VideoHistoryItem).storeName || 'Video Generation';
      if (item.type === 'listing') {
          const lItem = item as ListingHistoryItem;
          // Safeguard against missing output/title
          return lItem.output?.title || 'Listing Optimization';
      }
      
      const imgItem = item as ImageHistoryItem;
      if ((imgItem as any).name) return (imgItem as any).name;
      
      if (imgItem.metadata?.sku) return `Mockup: ${imgItem.metadata.sku}`;
      if (imgItem.tool === 'redesign' && imgItem.metadata?.instruction) {
          const instr = imgItem.metadata.instruction;
          return `Redesign: ${instr.length > 30 ? instr.slice(0, 30) + '...' : instr}`;
      }
      if (imgItem.tool === 'clone') return 'Design Extraction';
      
      const toolName = imgItem.tool || 'Unknown';
      return `${toolName.charAt(0).toUpperCase() + toolName.slice(1)} Task`;
  };

  const filteredItems = items.filter(item => {
    if (filter !== 'all' && item.type !== filter) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    
    if (item.ownerEmail && item.ownerEmail.toLowerCase().includes(query)) return true;

    const name = getJobName(item).toLowerCase();
    if (name.includes(query)) return true;

    if (item.type === 'video') {
      const vItem = item as VideoHistoryItem;
      return vItem.status.toLowerCase().includes(query);
    } else if (item.type === 'listing') {
      const lItem = item as ListingHistoryItem;
      return lItem.output?.description?.toLowerCase().includes(query);
    } 
    return false;
  });

  return (
    <div className="space-y-6">
      <ShareModal 
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        onConfirm={submitShare}
        onRevoke={revokeShare}
        currentSharedWith={itemToShare?.sharedWith}
        title={itemToShare ? `Share ${itemToShare.type}` : 'Share Access'}
      />

      <ConfirmModal 
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        description={confirmConfig.description}
        isDestructive={confirmConfig.isDestructive}
        isLoading={isConfirmLoading}
        confirmText={confirmConfig.isDestructive ? "Remove" : "Confirm"}
      />

      {/* Header Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 shrink-0">
          <Clock className="text-primary-600" /> Activity History
        </h2>
        
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
          <div className="relative w-full sm:w-64">
             <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
             <input 
               type="text" 
               placeholder="Search..." 
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
               className="w-full pl-9 pr-4 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
             />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto">
            <div className="flex items-center bg-slate-100 rounded-lg p-1 shrink-0">
                <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${filter === 'all' ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500'}`}>All</button>
                <button onClick={() => setFilter('listing')} className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 ${filter === 'listing' ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500'}`}><FileText size={14} /> Text</button>
                <button onClick={() => setFilter('image')} className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 ${filter === 'image' ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500'}`}><ImageIcon size={14} /> Images</button>
                <button onClick={() => setFilter('video')} className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 ${filter === 'video' ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500'}`}><Video size={14} /> Videos</button>
            </div>
            
            <div className="flex items-center bg-slate-100 rounded-lg p-1 shrink-0 ml-2">
                <button onClick={() => setViewMode('grid')} title="Grid View" className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-500'}`}><LayoutGrid size={18} /></button>
                <button onClick={() => setViewMode('list')} title="List View" className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-500'}`}><ListIcon size={18} /></button>
            </div>

            <button onClick={() => { setLoading(true); fetchHistory(); }} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 border border-slate-200 ml-2"><RefreshCw size={18} /></button>
          </div>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary-600" size={32} /></div>
      ) : error ? (
        <div className="p-4 bg-red-50 text-red-600 border border-red-200 rounded-lg flex items-center gap-2"><AlertCircle size={18} /> {error}</div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200 border-dashed">
          <p className="text-slate-500 font-medium">No items found</p>
        </div>
      ) : (
        <>
            {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredItems.map((item) => {
                    const name = getJobName(item);
                    if (item.type === 'video') return <VideoCard key={item.id} item={item as VideoHistoryItem} name={name} onDelete={handleDelete} onShare={openShareModal} onDownload={handleDownloadItem} onRetry={handleRetryVideo} onClick={() => setSelectedItem(item)} />;
                    if (item.type === 'listing') return <ListingCard key={item.id} item={item as ListingHistoryItem} name={name} onDelete={handleDelete} onShare={openShareModal} onClick={() => setSelectedItem(item)} />;
                    return <ImageCard key={item.id} item={item as ImageHistoryItem} name={name} onDelete={handleDelete} onShare={openShareModal} onDownload={handleDownloadItem} onClick={() => setSelectedItem(item)} />;
                })}
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <table className="w-full text-left text-sm text-slate-600">
                        <thead className="bg-slate-50 border-b border-slate-200 font-semibold text-slate-800">
                            <tr>
                                <th className="px-6 py-4">Type</th>
                                <th className="px-6 py-4">Job Name</th>
                                <th className="px-6 py-4">Date</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredItems.map(item => (
                                <tr key={item.id} onClick={() => setSelectedItem(item)} className="hover:bg-slate-50 cursor-pointer transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            {item.type === 'video' ? <Video size={16} className="text-blue-600" /> : item.type === 'listing' ? <FileText size={16} className="text-indigo-600" /> : <ImageIcon size={16} className="text-emerald-600" />}
                                            <span className="capitalize">{item.type}</span>
                                            {(item as VideoHistoryItem).listingData && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">+Listing</span>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-slate-900 max-w-md truncate">
                                        {getJobName(item)}
                                        {item.isSharedWithMe && <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">Shared</span>}
                                    </td>
                                    <td className="px-6 py-4">{new Date(item.createdAt).toLocaleDateString()}</td>
                                    <td className="px-6 py-4">
                                        {item.type === 'video' ? (
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${{ done: 'bg-emerald-100 text-emerald-700', processing: 'bg-blue-100 text-blue-700', pending: 'bg-amber-100 text-amber-700', error: 'bg-red-100 text-red-700', cancelled: 'bg-slate-100 text-slate-600' }[(item as VideoHistoryItem).status]}`}>
                                                {(item as VideoHistoryItem).status}
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">Completed</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            {item.type === 'video' && (item as VideoHistoryItem).status === 'error' && !item.isSharedWithMe && (
                                                <button onClick={(e) => handleRetryVideo(item as VideoHistoryItem, e)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Retry Generation">
                                                    <RefreshCw size={16} />
                                                </button>
                                            )}
                                            {((item.type === 'video' && (item as VideoHistoryItem).url) || (item.type === 'image' && (item as ImageHistoryItem).output)) && (
                                                <button onClick={(e) => handleDownloadItem(item, e)} className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors" title="Download">
                                                    <Download size={16} />
                                                </button>
                                            )}
                                            {!item.isSharedWithMe && (
                                                <button onClick={(e) => openShareModal(item, e)} className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors" title="Share">
                                                    <Share2 size={16} />
                                                </button>
                                            )}
                                            
                                            <button onClick={(e) => handleDelete(item, e)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title={item.isSharedWithMe ? "Remove View" : "Delete"}>
                                                {item.isSharedWithMe ? <LogOut size={16} /> : <Trash2 size={16} />}
                                            </button>
                                            
                                            {item.isSharedWithMe && <span title="Shared by another user"><Users size={16} className="text-purple-400" /></span>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
      )}

      {selectedItem && (
        <DetailsModal 
          item={selectedItem} 
          jobName={getJobName(selectedItem)}
          onClose={() => setSelectedItem(null)} 
          onDelete={handleDelete}
          onDownload={handleDownloadItem}
          onRetry={handleRetryVideo}
          isDownloading={isDownloading}
        />
      )}
    </div>
  );
};

const CopyButton = ({ text, label, className = "ml-2" }: { text: string, label?: string, className?: string }) => {
  const { addToast } = useToast();
  const [copied, setCopied] = useState(false);
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    addToast(`Copied ${label || 'text'} to clipboard`, 'info');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className={`text-slate-400 hover:text-primary-600 transition-colors inline-flex items-center ${className}`} title={`Copy ${label || 'text'}`}>
      {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
    </button>
  );
};

// Reorganized Badge Components
const SharedBadge = ({ item }: { item: HistoryItem }) => {
    if (item.isSharedWithMe) {
        // Bottom Left
        return <div className="absolute bottom-2 left-2 bg-purple-600 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm z-20 flex items-center gap-1 max-w-[120px] truncate"><Users size={10} /> Shared by {item.ownerEmail?.split('@')[0]}</div>;
    }
    if (item.sharedWith && item.sharedWith.length > 0) {
        return <div className="absolute bottom-2 left-2 bg-blue-600 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm z-20 flex items-center gap-1"><Users size={10} /> Shared ({item.sharedWith.length})</div>;
    }
    return null;
}

const ActionButtons = ({ item, onDelete, onShare, onDownload, onRetry }: any) => {
    // Top Right
    return (
        <div className="absolute top-2 right-2 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {item.type === 'video' && item.status === 'error' && !item.isSharedWithMe && (
                <button onClick={(e) => onRetry(item, e)} className="p-1.5 bg-black/50 text-white rounded-full hover:bg-blue-600 transition-colors" title="Retry Generation">
                    <RefreshCw size={14} />
                </button>
            )}
            {!item.isSharedWithMe && (
                <button onClick={(e) => onShare(item, e)} className="p-1.5 bg-black/50 text-white rounded-full hover:bg-primary-600 transition-colors" title="Share">
                    <Share2 size={14} />
                </button>
            )}
            {((item.type === 'video' && item.status === 'done') || item.type === 'image') && (
                <button onClick={(e) => onDownload(item, e)} className="p-1.5 bg-black/50 text-white rounded-full hover:bg-emerald-600 transition-colors" title="Download">
                    <Download size={14} />
                </button>
            )}
            <button onClick={(e) => onDelete(item, e)} className="p-1.5 bg-black/50 text-white rounded-full hover:bg-red-600 transition-colors" title={item.isSharedWithMe ? "Remove View" : "Delete"}>
                {item.isSharedWithMe ? <LogOut size={14} /> : <Trash2 size={14} />}
            </button>
        </div>
    );
};

const VideoCard = ({ item, name, onDelete, onShare, onDownload, onRetry, onClick }: any) => {
  const statusColor = { done: 'bg-emerald-100 text-emerald-700', processing: 'bg-blue-100 text-blue-700', pending: 'bg-amber-100 text-amber-700', error: 'bg-red-100 text-red-700', cancelled: 'bg-slate-100 text-slate-600' };
  
  return (
    <div onClick={onClick} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-all relative group cursor-pointer h-full">
      <ActionButtons item={item} onDelete={onDelete} onShare={onShare} onDownload={onDownload} onRetry={onRetry} />
      
      <div className="h-48 bg-slate-900 relative flex items-center justify-center group-hover:bg-slate-800 transition-colors">
        <Video className="text-slate-600 w-12 h-12" />
        {item.status === 'done' && <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100"><Play className="text-white fill-white w-10 h-10" /></div>}
        
        {item.status === 'error' && (
           <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[1px]">
               <button onClick={(e) => onRetry(item, e)} className="px-3 py-2 bg-white rounded-full text-xs font-bold text-slate-800 flex items-center gap-2 hover:bg-blue-50 hover:text-blue-700 shadow-lg transform hover:scale-105 transition-all">
                  <RefreshCw size={14} /> Retry
               </button>
           </div>
        )}
        
        {/* Status Badge: Bottom Right */}
        <div className={`absolute bottom-2 right-2 px-2 py-1 rounded-full text-xs font-bold ${statusColor[item.status as keyof typeof statusColor]}`}>{item.status}</div>
        
        {/* Extra Info: Top Center-ish or Below Actions? Lets put it Top-Left under Type */}
        {item.listingData && <div className="absolute top-2 right-2 mt-8 px-1.5 py-0.5 bg-indigo-500/90 text-white text-[10px] font-bold rounded z-10 opacity-80">+Listing</div>}
        
        {/* Type Badge: Top Left */}
        <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-md text-white text-xs font-bold px-2 py-1 rounded uppercase z-10">VIDEO</div>
        
        {/* Shared Badge: Bottom Left */}
        <SharedBadge item={item} />
      </div>
      <div className="p-4 flex-1">
        <div className="flex gap-2 text-xs text-slate-500 mb-2"><Calendar size={12} /> {new Date(item.createdAt).toLocaleDateString()}</div>
        <h3 className="font-semibold text-slate-800 mb-1 truncate">{name}</h3>
      </div>
    </div>
  );
};

const ImageCard = ({ item, name, onDelete, onShare, onDownload, onClick }: any) => {
  const mainImage = item.output ? (typeof item.output === 'string' ? item.output : item.output[0]?.url) : '';
  return (
    <div onClick={onClick} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-all group relative cursor-pointer h-full">
      <ActionButtons item={item} onDelete={onDelete} onShare={onShare} onDownload={onDownload} />

      <div className="h-48 bg-slate-100 relative overflow-hidden">
        {mainImage ? (
           <img src={mainImage} alt="Generated" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
           <div className="w-full h-full flex items-center justify-center text-slate-400"><ImageIcon size={32} /></div>
        )}
        {/* Type Badge: Top Left */}
        <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-md text-white text-xs font-bold px-2 py-1 rounded uppercase z-10">{item.tool || 'Image'}</div>
        
        {/* Shared Badge: Bottom Left */}
        <SharedBadge item={item} />
      </div>
      <div className="p-4 flex-1">
        <div className="flex gap-1 text-xs text-slate-500 mb-2"><Calendar size={12} /> {new Date(item.createdAt).toLocaleDateString()}</div>
        <p className="font-semibold text-slate-800 text-sm line-clamp-2">
             {name}
        </p>
      </div>
    </div>
  );
};

const ListingCard = ({ item, name, onDelete, onShare, onClick }: any) => {
  const description = item.output?.description || '';
  const tagsStr = item.output?.tags || '';

  return (
    <div onClick={onClick} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-all group relative cursor-pointer h-full">
       <ActionButtons item={item} onDelete={onDelete} onShare={onShare} onDownload={() => {}} />
       
       <div className="h-48 bg-indigo-50 p-4 flex flex-col justify-center items-center text-center relative overflow-hidden group-hover:bg-indigo-100 transition-colors">
          <FileText size={32} className="text-indigo-400 mb-2" />
          <h3 className="font-bold text-indigo-900 line-clamp-2 px-2">{name}</h3>
          
          {/* Extra Info: Top Right (Offset) */}
          {item.output?.videoPrompt && <div className="absolute top-2 right-2 mt-8 px-1.5 py-0.5 bg-purple-500/90 text-white text-[10px] font-bold rounded z-10">+Video</div>}
          
          {/* Type Badge: Top Left */}
          <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-md text-white text-xs font-bold px-2 py-1 rounded uppercase z-10">LISTING</div>
          
          {/* Shared Badge: Bottom Left */}
          <SharedBadge item={item} />
       </div>
       <div className="p-4 flex-1 flex flex-col">
          <div className="flex gap-2 text-xs text-slate-500 mb-2"><Calendar size={12} /> {new Date(item.createdAt).toLocaleDateString()}</div>
          <p className="text-xs text-slate-600 line-clamp-3 mb-2">{description}</p>
          <div className="mt-auto pt-2 flex flex-wrap gap-1">
             {tagsStr ? tagsStr.split(',').slice(0, 3).map((tag: string, i: number) => (
                <span key={i} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{tag.trim()}</span>
             )) : null}
             {tagsStr && tagsStr.split(',').length > 3 && <span className="text-[10px] text-slate-400">+{tagsStr.split(',').length - 3}</span>}
          </div>
       </div>
    </div>
  );
};

const DetailsModal = ({ item, jobName, onClose, onDelete, onDownload, onRetry, isDownloading }: any) => {
  const isVideo = item.type === 'video';
  const isListing = item.type === 'listing';
  const isImage = item.type === 'image';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col md:flex-row" onClick={e => e.stopPropagation()}>
         {/* Preview Section */}
         <div className={`w-full md:w-1/2 bg-slate-900 flex items-center justify-center relative ${isListing ? 'bg-indigo-50' : ''} min-h-[300px]`}>
            {isVideo && (
               item.status === 'done' && item.url ? (
                  <video src={item.url} controls className="max-w-full max-h-full" />
               ) : (
                  <div className="text-center text-slate-400 flex flex-col items-center">
                     <Video size={48} className="mx-auto mb-4 opacity-50" />
                     <p>Video {item.status}</p>
                     {item.status === 'error' && !item.isSharedWithMe && (
                         <button onClick={(e) => onRetry(item, e)} className="mt-4 px-4 py-2 bg-white text-slate-900 rounded-full font-bold text-sm hover:bg-slate-200 transition-colors flex items-center gap-2">
                             <RefreshCw size={14} /> Retry Generation
                         </button>
                     )}
                  </div>
               )
            )}
            {isImage && (
               <div className="relative w-full h-full flex items-center justify-center p-4 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]">
                  {Array.isArray(item.output) ? (
                      <div className="grid grid-cols-2 gap-2 w-full h-full overflow-y-auto">
                          {item.output.map((img: any, i: number) => (
                              <img key={i} src={img.url} className="w-full h-auto object-contain rounded-lg border border-slate-700" />
                          ))}
                      </div>
                  ) : (
                      <img src={item.output} className="max-w-full max-h-full object-contain" />
                  )}
               </div>
            )}
            {isListing && (
               <div className="w-full h-full flex items-center justify-center relative">
                  {item.output?.originalImage ? (
                      <img src={item.output.originalImage} className="max-w-full max-h-full object-contain opacity-90" alt="Product" />
                  ) : (
                      <div className="text-center p-8">
                         <FileText size={64} className="text-indigo-300 mb-4 mx-auto" />
                         <h3 className="font-bold text-indigo-900 mb-2">{jobName}</h3>
                         <div className="inline-flex gap-2 text-indigo-600 text-xs font-mono bg-white px-2 py-1 rounded border border-indigo-100">
                            <span>Title + Desc + Tags</span>
                         </div>
                      </div>
                  )}
               </div>
            )}
         </div>

         {/* Details Sidebar */}
         <div className="w-full md:w-1/2 flex flex-col bg-white h-full max-h-[50vh] md:max-h-[90vh]">
            <div className="p-6 border-b border-slate-100 flex justify-between items-start">
               <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-1 truncate max-w-[200px]" title={jobName}>
                     {jobName}
                  </h2>
                  <p className="text-sm text-slate-500 flex items-center gap-2">
                     <Calendar size={14} /> {new Date(item.createdAt).toLocaleString()}
                     {item.isSharedWithMe && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-bold">Shared by {item.ownerEmail}</span>}
                  </p>
               </div>
               <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={24} /></button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Content... (Same as before) */}
                {isVideo && (
                    <>
                       <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2 flex justify-between">
                              Video Prompt <CopyButton text={item.videoPrompt} label="Prompt" />
                          </label>
                          <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-700 font-mono whitespace-pre-wrap">{item.videoPrompt}</div>
                       </div>
                       
                       {item.listingData && (
                           <div className="space-y-4 pt-4 border-t border-slate-100">
                               <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                  <FileText size={16} className="text-indigo-600" /> Associated Listing Data
                               </h3>
                               
                               <div>
                                   <label className="text-xs font-bold text-slate-400 uppercase block mb-1 flex justify-between">
                                       Title <CopyButton text={item.listingData.title} label="Title" />
                                   </label>
                                   <div className="text-sm font-medium text-slate-800">{item.listingData.title}</div>
                               </div>
                               {/* ... other listing fields ... */}
                           </div>
                       )}
                    </>
                )}
                {isImage && (
                    <>
                        <div>
                             <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Tool Used</label>
                             <div className="flex items-center gap-2">
                                <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium capitalize">{item.tool}</span>
                             </div>
                        </div>
                        {/* ... params ... */}
                    </>
                )}
                {isListing && item.output && (
                    <div className="space-y-4">
                        {item.metadata?.language === 'vi' && (
                             <div className="mb-2 inline-block px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded">
                                 Vietnamese Analysis
                             </div>
                        )}
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase block mb-1 flex justify-between">
                                Title <CopyButton text={item.output.title} label="Title" />
                            </label>
                            <div className="text-sm font-medium text-slate-800">{item.output.title}</div>
                        </div>
                        {/* ... desc/tags ... */}
                    </div>
                )}
                
                {/* Share Info */}
                {!item.isSharedWithMe && item.sharedWith && item.sharedWith.length > 0 && (
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Shared With</label>
                        <div className="flex flex-wrap gap-2">
                            {item.sharedWith.map((email: string) => (
                                <span key={email} className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs border border-purple-100 flex items-center gap-1">
                                    <Users size={10} /> {email}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
               <button onClick={(e) => { onDelete(item, e); onClose(); }} className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center gap-2 px-3 py-2 hover:bg-red-50 rounded-lg transition-colors">
                  {item.isSharedWithMe ? <LogOut size={16} /> : <Trash2 size={16} />}
                  {item.isSharedWithMe ? "Remove View" : "Delete"}
               </button>
               
               <div className="flex gap-3 ml-auto">
                  {(item.url || item.output) && (
                      <>
                        <button 
                            onClick={(e) => onDownload(item, e)}
                            disabled={isDownloading}
                            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium shadow-sm flex items-center gap-2 disabled:opacity-50"
                        >
                            {isDownloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                            {Array.isArray(item.output) && item.output.length > 1 ? `Download All (${item.output.length})` : 'Download'}
                        </button>
                      </>
                  )}
                  {item.isSharedWithMe && (
                      <button disabled className="px-4 py-2 bg-slate-100 text-slate-400 rounded-lg text-sm font-medium flex items-center gap-2 cursor-not-allowed">
                         Read Only
                      </button>
                  )}
               </div>
            </div>
         </div>
      </div>
    </div>
  );
};